import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

interface CustomThemeProviderProps {
  children: ReactNode;
}

// Ash & Volcano Color Palette 🌋
const ashLavaColors = {
  // Teal: cool accent color for buttons and interactive elements
  teal: {
    lightest: '#e0f7fa', // Very light teal
    light: '#4dd0e1', // Light teal
    main: '#00bcd4', // Vibrant teal/cyan
    dark: '#0097a7', // Deep teal
    darker: '#00838f', // Darker teal
  },
  // Lava: warm accents for highlights, progress bars, warnings
  lava: {
    light: '#ff7b54', // Bright lava glow
    main: '#ff5722', // Molten orange
    dark: '#d84315', // Deep ember
    darker: '#bf360c', // Cooled lava
  },
  // Ash: dark charcoal backgrounds
  ash: {
    lightest: '#B8B9BD', // Light ash (text)
    light: '#73757A', // Medium ash
    medium: '#4a4c52', // Dark ash
    dark: '#24262b', // Paper/card background
    darker: '#1c1d21', // Darker surface
    darkest: '#131416', // Main background
    black: '#0c0d0e', // Deep volcanic black
  },
};

export const CustomThemeProvider: React.FC<CustomThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('theme-mode') as ThemeMode;
    // Default to dark mode for Ash & Lava theme
    return savedMode || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1350, // change this value
        xl: 1536,
      },
    },
    palette: {
      mode,
      primary: {
        light: ashLavaColors.teal.light,
        main: ashLavaColors.teal.main,
        dark: ashLavaColors.teal.dark,
        contrastText: '#ffffff',
      },
      secondary: {
        light: ashLavaColors.lava.light,
        main: ashLavaColors.lava.main,
        dark: ashLavaColors.lava.dark,
        contrastText: '#ffffff',
      },
      background: {
        default: mode === 'light' ? '#fafafa' : ashLavaColors.ash.darkest,
        paper: mode === 'light' ? '#ffffff' : ashLavaColors.ash.dark,
      },
      text: {
        primary: mode === 'light' ? '#1c1d21' : '#ffffff',
        secondary: mode === 'light' ? '#4a4c52' : ashLavaColors.ash.lightest,
      },
      error: {
        main: '#f44336',
      },
      warning: {
        main: ashLavaColors.lava.main,
      },
      success: {
        main: '#4caf50',
      },
      divider: mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.08)',
    },
    typography: {
      fontFamily: 'Inter, Roboto, Arial, sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundImage:
              mode === 'dark'
                ? `linear-gradient(145deg, ${ashLavaColors.ash.darkest} 0%, ${ashLavaColors.ash.black} 100%)`
                : undefined,
            minHeight: '100vh',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow:
              mode === 'light' ? '0 2px 8px rgba(0,0,0,0.1)' : '0 4px 20px rgba(0,0,0,0.5)',
            border: mode === 'dark' ? `1px solid ${ashLavaColors.ash.medium}` : undefined,
            backgroundImage:
              mode === 'dark'
                ? `linear-gradient(145deg, ${ashLavaColors.ash.dark} 0%, ${ashLavaColors.ash.darker} 100%)`
                : undefined,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage:
              mode === 'dark'
                ? `linear-gradient(145deg, ${ashLavaColors.ash.dark} 0%, ${ashLavaColors.ash.darker} 100%)`
                : undefined,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            transition: 'all 0.25s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow:
                mode === 'dark'
                  ? `0 4px 20px rgba(0, 188, 212, 0.35)`
                  : '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
          containedPrimary: {
            background: `linear-gradient(145deg, ${ashLavaColors.teal.main} 0%, ${ashLavaColors.teal.dark} 100%)`,
            '&:hover': {
              background: `linear-gradient(145deg, ${ashLavaColors.teal.light} 0%, ${ashLavaColors.teal.main} 100%)`,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? ashLavaColors.lava.main : ashLavaColors.ash.dark,
            backgroundImage:
              mode === 'dark'
                ? `linear-gradient(90deg, ${ashLavaColors.ash.dark} 0%, ${ashLavaColors.ash.darker} 100%)`
                : undefined,
            boxShadow: mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.5)' : undefined,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor:
                mode === 'dark' ? 'rgba(0, 188, 212, 0.12)' : 'rgba(0, 188, 212, 0.08)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          colorPrimary: {
            backgroundColor: mode === 'dark' ? 'rgba(0, 188, 212, 0.15)' : undefined,
            border: mode === 'dark' ? '1px solid rgba(0, 188, 212, 0.3)' : undefined,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: mode === 'dark' ? ashLavaColors.ash.medium : undefined,
          },
          bar: {
            background: `linear-gradient(90deg, ${ashLavaColors.lava.darker} 0%, ${ashLavaColors.lava.main} 50%, ${ashLavaColors.lava.light} 100%)`,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            // Default: no blur or dark background
            backdropFilter: 'none',
            backgroundColor: 'transparent',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            backgroundImage:
              mode === 'dark'
                ? `linear-gradient(145deg, ${ashLavaColors.ash.dark} 0%, ${ashLavaColors.ash.darker} 100%)`
                : undefined,
            border: mode === 'dark' ? `1px solid ${ashLavaColors.ash.medium}` : undefined,
            boxShadow:
              mode === 'dark'
                ? '0 8px 40px rgba(0,0,0,0.7), 0 0 60px rgba(0, 188, 212, 0.08)'
                : undefined,
          },
          root: {
            // Apply blur only for dialogs
            '& .MuiBackdrop-root': {
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(10, 10, 10, 0.85)',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundImage:
              mode === 'dark'
                ? `linear-gradient(145deg, ${ashLavaColors.ash.dark} 0%, ${ashLavaColors.ash.darker} 100%)`
                : undefined,
            border: mode === 'dark' ? `1px solid ${ashLavaColors.ash.medium}` : undefined,
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          slotProps: {
            paper: {
              sx: {
                backgroundImage:
                  mode === 'dark'
                    ? `linear-gradient(145deg, ${ashLavaColors.ash.dark} 0%, ${ashLavaColors.ash.darker} 100%)`
                    : undefined,
              },
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? ashLavaColors.ash.dark : undefined,
            border: mode === 'dark' ? `1px solid ${ashLavaColors.ash.medium}` : undefined,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: mode === 'dark' ? ashLavaColors.ash.light : undefined,
          },
        },
      },
    },
  });

  const value: ThemeContextType = {
    mode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a CustomThemeProvider');
  }
  return context;
};
